import { Field, Form, Formik } from 'formik'
import { FC } from 'react'
import {
  CheckBoxField,
  DateField,
  MultiSelectField,
  TextAreaField,
  TextField,
} from '@/shared/ui/FormFields'
import { Button } from '@/shared/ui/Button/Button'
import { Loader } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import ImageCropProvider from '@/app/providers/ImageCropProvider'
import PhotoUploder from '@/features/Photos/UploadAndFrontCrop/PhotoUploader'
import { editPromotionFormSchema } from '../lib/consts'
import { useEditPromotionForm } from '../lib/use-edit-promotion-form'
import { useMultiSelectWithSearch } from '@/shared/lib/useMultiSelectWithSearch'

type EditPromotionFormProps = {
  currentPromotionId: number
  onCloseModal: () => void
}

export const EditPromotionForm: FC<EditPromotionFormProps> = props => {
  const { currentPromotionId, onCloseModal } = props
  const { t } = useTranslation(['promotion', 'common'])
  const {
    initialValues,
    isLoadingEditPromotion,
    isErrorPromotion,
    isLoadingPromotion,
    submitForm,
    isLoadingSelected,
    isLoadingDefaults,
    allOptions,
    setSearchQuery,
  } = useEditPromotionForm(currentPromotionId, onCloseModal)

  const { createOptionsWithPreservedSelections, saveSelectedOptions } = useMultiSelectWithSearch()

  if (isErrorPromotion) {
    return <>{t('common:error_messages.something_went_wrong')}</>
  }

  if (isLoadingPromotion || (initialValues.residential_projects && isLoadingSelected)) {
    return (
      <div className='flex flex-col py-8 items-center justify-center'>
        <Loader size='xs' color='blue' />
      </div>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      enableReinitialize
      validationSchema={editPromotionFormSchema(t)}
    >
      {({ values, setFieldValue, setFieldTouched }) => {
        const residentialOptions = createOptionsWithPreservedSelections(
          allOptions,
          values.residential_projects || [],
        )
        return (
          <Form>
            <div className='flex flex-row items-center space-x-4 mb-4 pt-4'>
              <CheckBoxField
                name='is_permanent'
                label={t('promotion:is_permanent')}
                className='flex flex-col w-full mb-4'
                onChangeChecked={checked => {
                  if (checked) {
                    setFieldValue('end_date', null)
                    setFieldTouched('end_date', false, false)
                  }
                }}
                disabled={isLoadingEditPromotion}
              />
            </div>
            <TextField
              name='name'
              label={t('promotion:name')}
              className='flex flex-col w-full mb-4'
              required
              disabled={isLoadingEditPromotion}
            />

            <div className='flex flex-row items-center space-x-4'>
              <DateField
                name='start_date'
                label={t('promotion:start_date')}
                className='flex flex-col w-full mb-4'
                required
              />
              <DateField
                name='end_date'
                label={t('promotion:end_date')}
                className='flex flex-col w-full mb-4'
                required={!values.is_permanent}
                disabled={!!values.is_permanent}
              />
            </div>
            <MultiSelectField
              name='residential_projects'
              label={t('promotion:projects')}
              className='flex flex-col w-full mb-4'
              options={residentialOptions}
              isSearchable={true}
              required
              disabled={isLoadingEditPromotion}
              nothingFound={
                isLoadingSelected || isLoadingDefaults ? (
                  <div className='flex w-full items-center flex-col'>
                    <div className='flex w-[1.25rem] h-[1.25rem] justify-center items-center'>
                      <Loader size='xs' color='blue' />
                    </div>
                  </div>
                ) : (
                  t('common:nothing_found')
                )
              }
              onSearchChange={setSearchQuery}
              onSelectionChange={selectedIds => {
                saveSelectedOptions(allOptions, selectedIds)
              }}
            />
            <Field name='thumbnail'>
              {({ form: { values, setFieldValue } }) => (
                <ImageCropProvider>
                  <PhotoUploder
                    label={t('promotion:thumbnail')}
                    initialValue={values.thumbnail}
                    name={'thumbnail'}
                    setField={setFieldValue}
                    aspectRetio={4 / 3}
                  />
                </ImageCropProvider>
              )}
            </Field>
            <TextAreaField
              name='description'
              label={t('promotion:description')}
              className='flex flex-col w-full mb-4'
              placeholder='#Markdown'
              required
            />
            <div className='flex flex-row w-full'>
              <Button type='submit' size='md' color='base' disabled={isLoadingEditPromotion}>
                {isLoadingEditPromotion ? (
                  <Loader size='xs' color='white' />
                ) : (
                  t('promotion:buttons.save_changes')
                )}
              </Button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
